

import { defineComponent } from "vue";
import { mapStores, mapState } from "pinia";
import { NBackTop, NSpin } from "naive-ui";
import moment from "moment";

import StationList from '~/components/station-list/station-list.vue';
import Chart from '~/components/chart/chart.vue';
import AdvancedViewHeader from '~/pages/advanced-view/advanced-view-header.vue';
import BasePage from '~/core/base-page';

import { useAdvancedViewStore } from "~/store/advanced-view.store";
import { useStationStore } from "~/store/station.store";
import loading from "naive-ui/es/_internal/loading";
import { Station } from "~/api/models/station.model";

export default defineComponent({

    extends: BasePage,

    components: {
        AdvancedViewHeader,
        NBackTop,
        StationList,
        Chart,
        NSpin,
    },

    computed: {
        ...mapStores(useStationStore, useAdvancedViewStore),
        ...mapState(useAdvancedViewStore, ['indicators', 'mode', 'since', 'before', 'loading', 'selected']),
        ...mapState(useStationStore, ['allStations']),
    },

    async mounted(): Promise<void> {

        try {
            await this.stationStore.refresh();

            if(!this.stationStore.allStations || !this.stationStore.allStations.length) {
                this.$router.push({name: 'add-station'});
                return;
            }
    
            const mode = Array.isArray(this.$route.query?.mode) ? this.$route.query?.mode[0] : this.$route.query?.mode;
            if (mode && ['live', 'hourly'].includes(mode as string)) {
                this.advancedViewStore.setMode(mode as 'live' | 'hourly', false)
            }
    
            const since = Array.isArray(this.$route.query?.since) ? this.$route.query?.since[0] : this.$route.query?.since;
            if (this.$route.query?.since) {
                this.advancedViewStore.since = moment(since);
            }
    
            const before = Array.isArray(this.$route.query?.before) ? this.$route.query?.before[0] : this.$route.query?.before;
            if (this.$route.query?.before) {
                this.advancedViewStore.before = moment(before);
            }

            if(this.$route?.query?.station && !this.isStationAllowed(this.$route?.query?.station)) {
                this.$router.push('/403');
            }
    
            if (this.$route.query?.station) {
                await this.advancedViewStore.selectStation(Array.isArray(this.$route.query.station) ? this.$route.query.station : [this.$route.query.station]);
            } else {
                await this.advancedViewStore.selectStation([]);
            }

            await this.advancedViewStore.refresh();

        } catch(e) {
            this.showUnhandledError('Could not init advanced view', e);
        }
    },

    methods: {
        onStationForgotten(station: Station): void {
            const selected = this.selected
                .filter((s: Station) => station.id !== s.id) // remove forgotten station from selected
                .map((s: Station) => s.id);
            this.$router.replace({path: this.$route.path, query: { station: selected }});
        },

        isStationForgettable(station: Station): boolean {
            return station && station.group_name === 'aura-co2';
        },
    },

    watch: {
        $route(to, from) {
            let shouldUpdate: boolean = false;

            if (to?.query?.since !== from?.query?.since || to?.query?.before !== from?.query?.before) {
                const since = moment(to?.query?.since) || this.before;
                const before = moment(to?.query?.before) || this.since;
                this.advancedViewStore.setDatetimeRange(since, before, false);
                shouldUpdate = true;
            }

            if (to?.query?.mode !== from?.query?.mode) {
                this.advancedViewStore.setMode(to?.query?.mode, false);
                shouldUpdate = true;
            }

            if(to?.query?.station && !this.isStationAllowed(to?.query?.station)) {
                this.$router.push('/403')
            }

            if (to?.query?.station !== from?.query?.station) {
                this.advancedViewStore.selectStation(Array.isArray(to?.query?.station) ? to?.query?.station : [to?.query?.station])
                    .then(() => this.advancedViewStore.refresh());
                shouldUpdate = false;
            }

            if (shouldUpdate) {
                this.advancedViewStore.refresh();
            }

        },
    }
})

